import { Box, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PRECIPITATION_NAME, CHART_TITLE_PRECIPITACIONES } from '../../../constants/graphs';
import apiClient, { rainfallConfig } from '../../../requests/api/apiClient';
import { ConversationSchema, TimePointSchema } from '../../../requests/api/apiTypes';
import { ReduxState } from '../../../types';
import { navigateToChart } from '../../../utils/helpers';
import { FetchError } from '../../../views/types';
import BaseHighchart from '../BaseHighchart';
import ChartFilter from '../ChartFilter';
import ConversationModal from '../ConversationTab/conversationModal';
import {
  calculateYAxisLimits,
  getChartConfig,
  getConversationTooltip,
  getYAxis,
} from '../LineCharts/utils';
import { ChartDataType, ChartFiltersWeather } from '../types';
import ChartConfigDialog from '../ChartConfigDialog/ChartConfigDialog';
import RainfallConfigContent from './RainfallConfigContent';
import prepareData, { RainfallConfigByYear } from './builder';
import useChartConfigClick from '../../../hooks/useChartConfigClick';

// To Do: Actualizar types

function RainfallChart({
  chartView,
  sectorId,
  farmId,
  hideLegend = false,
  hideXValues = false,
  hideYValues = false,
  height = 300,
  title = CHART_TITLE_PRECIPITACIONES,
  yAxisTitle = 'Precipitaciones (mm)',
  externalModalState,
  externalSetModalState,
  externalConversations,
  externalSetConversations,
  onConversationCreated,
  refreshTrigger,
  isWeatherStation,
}: {
  chartView: 'dashboard' | 'graphs' | 'settings' | 'fullScreen'
  sectorId: string
  farmId: string
  hideLegend?: boolean
  hideXValues?: boolean
  hideYValues?: boolean
  height?: number | string
  title?: string
  yAxisTitle?: string
  isWeatherStation?: boolean
  externalModalState?: {
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  };
  externalSetModalState?: React.Dispatch<React.SetStateAction<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>>;
  externalConversations?: ConversationSchema[];
  externalSetConversations?: React.Dispatch<React.SetStateAction<ConversationSchema[]>>;
  onConversationCreated?: () => void;
  refreshTrigger?: number;
}) {
  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    categories: [],
    absoluteCategories: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [configByYear, setConfigByYear] = useState<RainfallConfigByYear>({});
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);

  const [internalModalState, setInternalModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [internalConversations, setInternalConversations] = useState<ConversationSchema[]>([]);

  const chartName = 'precipitaciones';

  const modalState = externalModalState || internalModalState;
  const setModalState = externalSetModalState || setInternalModalState;
  const conversations = externalConversations || internalConversations;
  const setConversations = externalSetConversations || setInternalConversations;
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);
  useEffect(() => {
    const fetchConfig = async () => {
      if (!sectorId) return;

      try {
        const { data } = await rainfallConfig.get(sectorId);
        if (data?.rainfallConfig?.years) {
          setConfigByYear(data.rainfallConfig.years);
        }
      } catch (error) {
        // Error fetching rainfall config
      }
    };

    fetchConfig();
  }, [sectorId, refreshTrigger]);

  const preparedFilters: ChartFiltersWeather = useMemo(() => ({
    farmId,
    startDate: interval?.startDate || '',
    endDate: interval?.endDate || '',
    weatherZoneId: selectedSector?.weatherZoneId,
    sn: selectedSector?.weatherZoneId,
    sensorType: 'Rain',
    sectorId,
    chart: PRECIPITATION_NAME,
    isWeatherStation,
  }), [farmId, interval, selectedSector?.weatherZoneId, isWeatherStation]);
  const columnWidth = Math.min(300 / dayjs(interval?.endDate).diff(dayjs(interval?.startDate), 'day'), 18);

  const createSeriesData = useCallback((preparedData: {
    rain: Array<{ x: number; y: number; custom: { date: string } } | null>;
    cumulativeRain: Array<{ x: number; y: number; custom: { date: string } } | null>;
  }) => [
    {
      type: 'column',
      name: 'Precipitaciones (mm)',
      data: preparedData.rain,
      yAxis: 0,
      pointWidth: columnWidth,
    },
    {
      type: 'line',
      name: 'Precipitaciones Acumuladas (mm)',
      data: preparedData.cumulativeRain,
      yAxis: 0,
    },
  ], [columnWidth]);

  // To Do: Actualizar types
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    if (!interval) return;
    try {
      const preparedData = await prepareData(
        preparedFilters,
        selectedSector?.telemetryType,
        configByYear,
      );

      const seriesData = createSeriesData(preparedData);

      setChartData({
        series: seriesData,
        absoluteCategories: preparedData.absoluteCategories,
      });
    } catch (error) {
      // Error fetching data
    } finally {
      setIsLoading(false);
    }
  }, [
    preparedFilters,
    interval,
    sectorId,
    configByYear,
    selectedSector?.telemetryType,
    createSeriesData,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchData();
    }
  }, [refreshTrigger, fetchData]);

  const customTooltip = useMemo(() => ({
    shared: true,
    useHTML: true,
    formatter(this: Highcharts.TooltipFormatterContextObject) {
      if (this.series.name === 'Comentarios') {
        return getConversationTooltip(this, conversations);
      }
      const header = '<div style="font-size: 14px; font-weight: bold; margin-bottom: 8px;">Resumen</div>';
      const subHeader = `<div style="font-size: 12px; margin-bottom: 8px;">${dayjs(this.x).format('MMM DD, YYYY')}</br></div>`;
      const points = this.points?.map(
        (point) => `<div style="margin: 4px 0"><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${Number(point.y).toFixed(2)}</b></div>`,
      ).join('');
      return `<div style="padding: 8px">${header}${subHeader}${points}</div>`;
    },
  }), [conversations]);

  useEffect(() => {
    if (!externalConversations) {
      const loadConversations = async () => {
        try {
          const { data } = await apiClient.conversation.conversationDetail(
            sectorId,
            PRECIPITATION_NAME,
          );
          setInternalConversations(data.conversations);
        } catch (err) {
          const { error } = err as FetchError;
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      };

      loadConversations();
    }
  }, [sectorId, externalConversations]);

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = useCallback(() => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  }, [history, farmId, sectorId]);

  const chartConfig = useMemo(() => {
    if (!interval) return null;

    const yLimits = calculateYAxisLimits(chartData.series as any[]);
    const baseYAxis = getYAxis(yAxisTitle, hideYValues) as Highcharts.YAxisOptions;

    const baseConfig = getChartConfig(
      chartData,
      height,
      hideXValues,
      {
        ...baseYAxis,
        min: 0,
        max: yLimits.max,
      },
      dayjs(interval.startDate).valueOf(),
      dayjs(interval.endDate).valueOf(),
    );

    return {
      ...baseConfig,
      plotOptions: {
        column: {
          pointPadding: 0,
          borderWidth: 0,
        },
      },
    };
  }, [
    hideXValues,
    chartData,
    hideYValues,
    yAxisTitle,
    height,
    interval?.startDate,
    interval?.endDate,
  ]);

  return (
    <>
      <Box>
        {isLoading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}
        <div style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
          <BaseHighchart
            chartView={chartView}
            sectorId={sectorId}
            hideYValues={hideYValues}
            filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
            title={title}
            legend={{ enabled: !hideLegend }}
            yAxisTitle={yAxisTitle}
            series={chartData.series as Highcharts.SeriesOptionsType[]}
            tooltip={customTooltip}
            onConfigClick={handleConfigClick}
            onFullScreenClick={handleFullScreenClick}
            setModalState={setModalState}
            conversations={conversations}
            absoluteCategories={chartData.absoluteCategories}
            hideXValues={hideXValues}
            chartConfig={chartConfig}
            height={height}
            interval={interval}
            zoomSync={true}
            autoAdjustYAxis={true}
            graphName={PRECIPITATION_NAME}
          />
        </div>
      </Box>
      <ConversationModal
        modalState={modalState}
        setModalState={setModalState}
        farmId={farmId}
        sectorId={sectorId}
        chart={PRECIPITATION_NAME}
        setConversationsFunction={setConversations}
        onConversationCreated={onConversationCreated}
      />
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · Precipitaciones"
      >
        <RainfallConfigContent
          sectorId={sectorId}
          onConfigSaved={() => fetchData()}
        />
      </ChartConfigDialog>
    </>
  );
}

export default RainfallChart;
